/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

// ── helpers ─────────────────────────────────────────────────────────────────

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return { Accept: "application/json", Authorization: `Bearer ${token}` };
}

async function apiFetch(path, options = {}) {
  const { headers: extraHeaders, params, ...rest } = options;
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await window.fetch(`${API_BASE}/${path}${qs}`, {
    headers: { ...authHeaders(), ...(extraHeaders || {}) },
    ...rest,
  });
  return res;
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function MemberAvatar({ member, size = 36 }) {
  const photo = member?.photo;
  const name = member?.name || "?";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <Avatar
      src={photo ? `${API_BASE}/media/${photo}` : undefined}
      sx={{ width: size, height: size, fontSize: size * 0.4, bgcolor: "primary.main" }}
    >
      {!photo && initials}
    </Avatar>
  );
}

// ── Post Testimony Dialog ────────────────────────────────────────────────────

function PostTestimonyDialog({ open, onClose, onPosted }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const handlePost = useCallback(async () => {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch("testimonies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: title.trim(), body: body.trim() }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      onPosted(data);
      setTitle("");
      setBody("");
      onClose();
    } catch {
      Swal.fire("Error", "Failed to post testimony", "error");
    } finally {
      setSaving(false);
    }
  }, [title, body, onClose, onPosted]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Share Your Testimony</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Share how God has worked in your life. Your testimony encourages others!
        </Typography>
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="e.g. God healed my family"
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label="Your Testimony"
          fullWidth
          multiline
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Tell us what God has done..."
          inputProps={{ maxLength: 5000 }}
        />
        <Typography variant="caption" color="text.secondary">
          {body.length}/5000
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePost}
          disabled={saving || !title.trim() || !body.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : <FavoriteIcon />}
          sx={{ bgcolor: "primary.main" }}
        >
          Share Testimony
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Reply Thread ─────────────────────────────────────────────────────────────

function ReplyThread({ testimonyId, replies: initialReplies, myMemberId, onReplyPosted }) {
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);
  const [replies, setReplies] = useState(initialReplies || []);

  useEffect(() => {
    setReplies(initialReplies || []);
  }, [initialReplies]);

  const handleSend = useCallback(async () => {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      const res = await apiFetch(`testimonies/${testimonyId}/replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: replyText.trim() }),
      });
      if (!res.ok) throw new Error();
      const reply = await res.json();
      setReplies((prev) => [...prev, reply]);
      setReplyText("");
      onReplyPosted?.();
    } catch {
      Swal.fire("Error", "Failed to post reply", "error");
    } finally {
      setPosting(false);
    }
  }, [replyText, testimonyId, onReplyPosted]);

  const handleDeleteReply = useCallback(async (replyId) => {
    const res = await apiFetch(`testimonies/replies/${replyId}`, { method: "DELETE" });
    if (res.ok) setReplies((prev) => prev.filter((r) => r.id !== replyId));
  }, []);

  return (
    <Box sx={{ pl: { xs: 1, sm: 2 }, borderLeft: "2px solid", borderColor: "divider" }}>
      {replies.map((reply) => (
        <Box key={reply.id} sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "flex-start" }}>
          <MemberAvatar member={reply.member} size={28} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ bgcolor: "grey.100", borderRadius: 2, px: 1.5, py: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.primary">
                {reply.member?.name}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.3 }}>
                {reply.message}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {timeAgo(reply.created_at)}
            </Typography>
          </Box>
          {(reply.member?.id === myMemberId || !myMemberId) && (
            <Tooltip title="Delete reply">
              <IconButton
                size="small"
                onClick={() => handleDeleteReply(reply.id)}
                sx={{ opacity: 0.5, "&:hover": { opacity: 1, color: "error.main" } }}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}

      {/* Reply input */}
      <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "flex-end" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Write a reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          multiline
          maxRows={3}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={posting || !replyText.trim()}
          sx={{ mb: 0.5 }}
        >
          {posting ? <CircularProgress size={18} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}

// ── Testimony Card ───────────────────────────────────────────────────────────

function TestimonyCard({ testimony, myMemberId, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [replyCount, setReplyCount] = useState(testimony.reply_count || 0);

  const isOwn = testimony.member?.id === myMemberId;

  const handleDelete = useCallback(async () => {
    const result = await Swal.fire({
      title: "Delete Testimony?",
      text: "This will also delete all replies.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;
    const res = await apiFetch(`testimonies/${testimony.id}`, { method: "DELETE" });
    if (res.ok) onDeleted(testimony.id);
  }, [testimony.id, onDeleted]);

  return (
    <Card
      elevation={1}
      sx={{
        mb: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        "&:hover": { boxShadow: 3 },
        transition: "box-shadow 0.2s",
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <MemberAvatar member={testimony.member} size={42} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {testimony.member?.name || "Member"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {timeAgo(testimony.created_at)}
            </Typography>
          </Box>
          {isOwn && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{ color: "error.light", opacity: 0.7, "&:hover": { opacity: 1 } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, mb: 1, fontSize: { xs: "1rem", sm: "1.1rem" } }}
        >
          {testimony.title}
        </Typography>

        {/* Body */}
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
          {testimony.body}
        </Typography>
      </CardContent>

      <CardActions sx={{ px: 2, pt: 0, pb: 1.5 }}>
        <Chip
          icon={<FavoriteIcon sx={{ fontSize: 14 }} />}
          label="Amen!"
          size="small"
          variant="outlined"
          color="primary"
          sx={{ mr: 1 }}
        />
        <Button
          size="small"
          onClick={() => setExpanded((p) => !p)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ ml: "auto", textTransform: "none" }}
        >
          {replyCount === 0 ? "Reply" : `${replyCount} Repl${replyCount === 1 ? "y" : "ies"}`}
        </Button>
      </CardActions>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <ReplyThread
            testimonyId={testimony.id}
            replies={testimony.replies || []}
            myMemberId={myMemberId}
            onReplyPosted={() => setReplyCount((c) => c + 1)}
          />
        </Box>
      </Collapse>
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function Testimonies() {
  const { member } = useAuth();
  const myMemberId = member?.id;

  const [testimonies, setTestimonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadTestimonies = useCallback(async (pageNum = 1, append = false) => {
    try {
      const res = await apiFetch("testimonies", { params: { page: pageNum } });
      if (!res.ok) return;
      const json = await res.json();
      setTestimonies((prev) => (append ? [...prev, ...json.data] : json.data));
      setLastPage(json.last_page || 1);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadTestimonies(1, false);
  }, [loadTestimonies]);

  const handlePosted = useCallback((newTestimony) => {
    setTestimonies((prev) => [newTestimony, ...prev]);
  }, []);

  const handleDeleted = useCallback((id) => {
    setTestimonies((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleLoadMore = useCallback(() => {
    const next = page + 1;
    setPage(next);
    setLoadingMore(true);
    loadTestimonies(next, true);
  }, [page, loadTestimonies]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Page header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            px: { xs: 0.5, sm: 0 },
          }}
        >
          <Box>
            <MDTypography variant="h5" fontWeight={700}>
              Testimonies
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Share and celebrate what God has done
            </MDTypography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ borderRadius: 8, px: { xs: 2, sm: 3 }, textTransform: "none" }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Share Testimony
            </Box>
            <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
              Share
            </Box>
          </Button>
        </Box>

        {/* Feed */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : testimonies.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              textAlign: "center",
              py: 6,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <FavoriteIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No testimonies yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Be the first to share how God has worked in your life!
            </Typography>
            <Button
              variant="contained"
              onClick={() => setDialogOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Share Your Testimony
            </Button>
          </Card>
        ) : (
          <>
            {testimonies.map((t) => (
              <TestimonyCard
                key={t.id}
                testimony={t}
                myMemberId={myMemberId}
                onDeleted={handleDeleted}
              />
            ))}
            {page < lastPage && (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  sx={{ textTransform: "none" }}
                >
                  {loadingMore ? <CircularProgress size={18} /> : "Load More"}
                </Button>
              </Box>
            )}
          </>
        )}

        <PostTestimonyDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onPosted={handlePosted}
        />
      </MDBox>
    </DashboardLayout>
  );
}
